import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Paper, Typography, Box, Grid } from '@mui/material';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import HistoryIcon from '@mui/icons-material/History';
import GroupIcon from '@mui/icons-material/Group';
import BusinessIcon from '@mui/icons-material/Business';
import SettingsIcon from '@mui/icons-material/Settings';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useSnackbar } from "notistack";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';


const QuickTiles = ({ currentUser, activeTab }) => {

	 const [scanCount, setScanCount] = useState(0);
   const [invoicesCount, setInvoicesCount] = useState(0);
   const [expensesCount, setExpensesCount] = useState(0);
  const [userCount, setUserCount] = useState(0);
  const [vendorCount, setVendorCount] = useState(0);
  const [employeeCount, setEmployeeCount] = useState(0);
  const [balance, setBalance] = useState(50); 

    const { enqueueSnackbar } = useSnackbar();

  const tileHeight = 200; // Set height to ensure square tiles
  const iconStyle = { color: '#007cc0', fontSize: 50 };
  const linkStyle = { color: 'grey', textDecoration: 'none', fontFamily: 'SapFiori72', fontSize: 'smaller' };
  const titleStyle = { fontFamily: 'SapFiori72', color: 'black', fontSize: 'larger' };
  const valueStyle = { fontFamily: 'SapFiori72', color: 'grey', fontWeight: 'bold', fontSize: 40, marginLeft: 5 };
  const sapColors = ["#0a6ed1", "#004b87", "#f0ab00", "#7ca10c", "#d72d42"];

  useEffect(() => {
    fetchStatistics();
    fetchCompanyData(); // Fetch company data after statistics
  }, []);

  // Consolidated fetchStatistics function
  const fetchStatistics = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/statistics`);
      if (!response.ok) {
        throw new Error('Failed to fetch statistics');
      }
      const data = await response.json();
      setScanCount(data.scans);
      setInvoicesCount(data.invoices);
      setExpensesCount(data.parent_expenses);
      setUserCount(data.users);
      setVendorCount(data.vendors);
      setEmployeeCount(data.employees);
    } catch (error) {
      console.error('Error fetching statistics:', error);
    }
  };

 // Fetch company data
 const fetchCompanyData = async () => {
  const companyId = currentUser?.employeeData?.company_id; // Safely get company ID
  if (companyId) {
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/get-companies?id=${companyId}`);
    if (response.ok) {
      const data = await response.json();
      if (data.company) {
        setBalance(data.company.balance || 0); // Set the company balance with a fallback to 0
      }
    }
  }
};

  // Calculate performed and remaining based on balance
  const totalBalance = 50;
  const performed = totalBalance - balance; // Calculate performed
  const remaining = balance; // Remaining is simply the balance

  const data = [
    { name: 'Balance', value: balance },
    { name: 'Performed', value: performed >= 0 ? performed : 0 }, // Ensure non-negative
    { name: 'Remaining', value: remaining >= 0 ? remaining : 0 }, // Ensure non-negative
  ];


 const handleLinkClick = () => {
    enqueueSnackbar("You are running the latest version of SmartIDP AI", { autoHideDuration: 3000 });
  };
  const handleChargeClick = () => {
    enqueueSnackbar("Feature not available in the demo. Contact SmartIDP Support to recharge your demo credits.", { autoHideDuration: 3000 });
  };

const Slider = ({ children }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = () => {
    setCurrentSlide((prevSlide) => (prevSlide === children.length - 1 ? 0 : prevSlide + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prevSlide) => (prevSlide === 0 ? children.length - 1 : prevSlide - 1));
  };

  const handleBulletClick = (index) => {
    setCurrentSlide(index);
  };

  return (
        <div style={{ position: 'relative', overflow: 'hidden', height: '100%', borderRadius: '20px' }}>

      <div style={{ display: 'flex', transition: 'transform 0.5s ease', transform: `translateX(-${currentSlide * 100}%)`, height: '100%' }}>
        {children.map((child, index) => (
          <div key={index} style={{ minWidth: '100%', height: '100%' }}>
            {child}
          </div>
        ))}
      </div>
      <div style={{ position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)', zIndex: 1 }}>
        {children.map((_, index) => (
          <button
            key={index}
            onClick={() => handleBulletClick(index)}
            style={{
              width: 10,
              height: 10,
              borderRadius: '100%',
              margin: '4px',
              background: currentSlide === index ? '#007cc0' : '#ccc',
              border: 'none',
              cursor: 'pointer',
              outline: 'none',
            }}
          />
        ))}
      </div>
      <button
        onClick={prevSlide}
        style={{
          position: 'absolute',
          top: '50%',
          left: 0,
          transform: 'translateY(-50%)',
          zIndex: 1,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
        }}
      >
        <ChevronLeftIcon style={{ fontSize: 40, color: 'white' }} />
      </button>
      <button
        onClick={nextSlide}
        style={{
          position: 'absolute',
          top: '50%',
          right: 0,
          transform: 'translateY(-50%)',
          zIndex: 1,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: 0,
        }}
      >
        <ChevronRightIcon style={{ fontSize: 40, color: 'white' }} />
      </button>
    </div>
  );
};

const VideoSlide1 = () => (
  <div style={{ height: '100%', position: 'relative' }}>
    <video
        autoPlay
        loop
        muted
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      >
        <source src="/banner-vid.mp4" type="video/mp4" />
        Your browser does not support the video tag.
      </video>
              <Box
                p={2} position="absolute" bottom={0} left={0} right={0} bgcolor="rgba(1, 40, 74, 0.8)"
              >
               <Typography variant="subtitle2" color="white !important" style={titleStyle}>You are running the beta version of SmartIDP<sup>AI</sup></Typography>
			<Link to="#" onClick={handleLinkClick}> 	  
<Typography color="white !important" style={{ fontFamily: 'SapFiori72' }}>Check for updates</Typography></Link>
              </Box>
  </div>
);
const VideoSlide2 = () => (
  <div style={{ height: '100%', position: 'relative' }}>
    <video
      autoPlay
      loop
      muted
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
      }}
    >
      <source src="/explanation-vid.mp4" type="video/mp4" />
      Your browser does not support the video tag.
    </video>
  </div>
);


  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: 4, border: '1px solid #354a5f', backgroundColor: 'rgba(255, 255, 255, 0.5)' }}>
    <Grid container spacing={2}>
      {/* First Row 
	      {currentUser && currentUser.can_add_invoice && ( */}
      <Grid item xs={12} sm={6} md={2}>
        <Paper elevation={3} style={{ height: tileHeight, borderRadius: '20px' }}>
          <Box p={2} display="flex" flexDirection="column" justifyContent="space-between" height="100%">
            <Typography variant="subtitle2" align="left" style={titleStyle}>New Scan</Typography>
            <Box display="flex" justifyContent="flex-end" alignItems="flex-end">
              <Box display="flex" flexDirection="column" alignItems="center">
                 <Link to="/scan"><CameraAltIcon style={iconStyle} /></Link>
                <Link to="/scan" style={linkStyle}>Scan Now</Link>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Grid>
		{/*  )} */}
    {/*  {currentUser && currentUser.can_access_scans && ( */}

    {activeTab === 0 && ( // Invoicing Overview
        <>
          <Grid item xs={12} sm={6} md={2}>
            <Paper elevation={3} style={{ height: tileHeight, borderRadius: '20px' }}>
              <Box p={2} display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                <Box>
                  <Typography variant="subtitle2" align="left" style={titleStyle}>Manage Invoices</Typography>
                  <Link to="/history" style={linkStyle}>All Invoices</Link>
                </Box>
                <Box display="flex" justifyContent="right" alignItems="center">
                  <HistoryIcon style={iconStyle} />
                  <Typography style={valueStyle}>{invoicesCount}</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
          {/* Add more tiles for Invoicing Overview as needed */}
        </>
      )}

      {activeTab === 2 && (  
        <>
          <Grid item xs={12} sm={6} md={2}>
            <Paper elevation={3} style={{ height: tileHeight, borderRadius: '20px' }}>
              <Box p={2} display="flex" flexDirection="column" justifyContent="space-between" height="100%">
                <Box>
                  <Typography variant="subtitle2" align="left" style={titleStyle}>Manage Expenses</Typography>
                  <Link to="/history" style={linkStyle}>All Expenses</Link>
                </Box>
                <Box display="flex" justifyContent="right" alignItems="center">
                  <HistoryIcon style={iconStyle} />
                  <Typography style={valueStyle}>{expensesCount}</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </>
      )}

	{/*  )} */}



      <Grid item xs={12} sm={6} md={6}>
        <Paper elevation={3} style={{ height: tileHeight, borderRadius: '20px'  }}>
          <Slider>
<VideoSlide1 />
  <VideoSlide2 />
          </Slider>
        </Paper>
      </Grid>


 {/*    {currentUser && currentUser.can_user_management && ( */}
      <Grid item xs={12} sm={6} md={2}>
  <Paper elevation={3} style={{ height: tileHeight, borderRadius: '20px'  }}>
    <Box p={2} display="flex" flexDirection="column" justifyContent="space-between" height="100%">
      <Box>
        <Typography variant="subtitle2" align="left" style={titleStyle}>Manage Users</Typography>
        <Link to="/users" style={linkStyle}>All Users</Link>
      </Box>
      <Box display="flex" justifyContent="right" alignItems="center">
        <GroupIcon style={iconStyle} />
              <Typography style={valueStyle}>{userCount}</Typography>
      </Box>
    </Box>
  </Paper>
</Grid>
{/* 	)} */}

{activeTab === 0 && ( // Invoicing Overview
        <>
      {/* Second Row */}
   <Grid item xs={12} sm={6} md={2}>
  <Paper elevation={3} style={{ height: tileHeight, borderRadius: '20px'  }}>
    <Box p={2} display="flex" flexDirection="column" justifyContent="space-between" height="100%">
      <Box>
        <Typography variant="subtitle2" align="left" style={titleStyle}>Manage Vendors</Typography>
        <Link to="/vendors" style={linkStyle}>All Vendors</Link>
      </Box>
      <Box display="flex" justifyContent="right" alignItems="center">
        <BusinessIcon style={iconStyle} />
              <Typography style={valueStyle}>{vendorCount}</Typography>
      </Box>
    </Box>
  </Paper>
</Grid>
</>
)}
{activeTab === 2 && ( // Expense Overview
        <>
      {/* Second Row */}
   <Grid item xs={12} sm={6} md={2}>
  <Paper elevation={3} style={{ height: tileHeight, borderRadius: '20px'  }}>
    <Box p={2} display="flex" flexDirection="column" justifyContent="space-between" height="100%">
      <Box>
        <Typography variant="subtitle2" align="left" style={titleStyle}>Manage Employees</Typography>
        <Link to="/vendors" style={linkStyle}>All Employees</Link>
      </Box>
      <Box display="flex" justifyContent="right" alignItems="center">
        <BusinessIcon style={iconStyle} />
              <Typography style={valueStyle}>{employeeCount}</Typography>
      </Box>
    </Box>
  </Paper>
</Grid>
</>
)}


      <Grid item xs={12} sm={6} md={6}>
        <Paper elevation={3} style={{ height: tileHeight, borderRadius: '20px'  }}>
          <Box p={2}>
            <Typography variant="h6" style={titleStyle}>My Subscription</Typography>
            <div style={{ width: '100%', height: 160 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={data}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} angle={-30} verticalAnchor="middle" />
                  <Tooltip />
                  <Bar dataKey="value" barSize={10}>
                    {data.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={sapColors[index]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Box>
        </Paper>
      </Grid>


      <Grid item xs={12} sm={6} md={2}>
        <Paper elevation={3} style={{ height: tileHeight, borderRadius: '20px'  }}>
          <Box p={2} display="flex" flexDirection="column" justifyContent="space-between" height="100%">
		   <Box>
            <Typography variant="subtitle2" align="left" style={titleStyle}>My Credits</Typography>
			              <Link style={linkStyle}  onClick={handleChargeClick}>Recharge Now</Link>
</Box>
            <Box display="flex" justifyContent="flex-end" alignItems="flex-end">
              <Box display="flex" flexDirection="right" alignItems="center">
                <CreditCardIcon style={iconStyle} />
				              <Typography style={valueStyle}>{balance}</Typography>

              </Box>
            </Box>
          </Box>
        </Paper>
      </Grid>

 {/*   {currentUser && currentUser.can_settings && ( */}
      <Grid item xs={12} sm={6} md={2}>
        <Paper elevation={3} style={{ height: tileHeight, borderRadius: '20px'  }}>
          <Box p={2} display="flex" flexDirection="column" justifyContent="space-between" height="100%">
            <Typography variant="subtitle2" align="left" style={titleStyle}>Preferences</Typography>
            <Box display="flex" justifyContent="flex-end" alignItems="flex-end">
              <Box display="flex" flexDirection="column" alignItems="center">
                <SettingsIcon style={iconStyle} />
                <Link to="/settings" style={linkStyle}>System Settings</Link>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Grid>
 {/* 	)} */}
    </Grid>
    </Paper>

  );
};

export default QuickTiles;
